import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { ColumnDef } from '@tanstack/react-table'
import { XmarkCircle } from 'iconoir-react'
import { compact } from 'lodash'
import moment from 'moment'
import type { UpdateContactInput } from 'app/generated/gql/graphql'
import PhoneNumber from 'app/components/PhoneNumber'
import { LazyWorkspaceCell } from 'app/components/LazyWorkspaceCell'
import { AnchorCell } from 'app/components/table/AnchorCell'
import { InlineEditCell } from 'app/components/table/InlineEditCell'
import TooltipWrapper from 'app/components/table/TooltipWrapper'
import { LazyLocationCell } from 'app/components/LazyLocationCell'
import { ChannelMappingCell } from 'app/components/contacts/ChannelMappingCell'
import { AdPlatformMappingCell } from 'app/components/contacts/AdPlatformMappingCell'
import TagsCell from 'app/components/tags/TagsCell'
import { ContactCell } from 'app/components/shared/ContactCell'
import { LeadOwnerAssignCell } from 'app/table-columns/leadOwnerCell'
import type { ContactJson, Permission } from 'app/types/entities'
import { currentUrlWithParam } from 'lib/currentUrlWithParam'

/** Signature of a contact-column factory: the shared parameter list that each
 * contact table's column builder (and the `customColumns` prop) accepts. */
export type ContactColumnsFactory = (
  contactHandler: (contactId: string, patientIds?: string[]) => void,
  updateContact: (contact: UpdateContactInput & { id?: string }) => void,
  closeDrawerHandler: () => void,
  isSMC: boolean,
  isSuper: boolean,
) => ColumnDef<ContactJson, any>[]

/** Address cell: joins the contact's two address lines. Shared by the contact
 * tables (attribution-contacts and marketing-details). */
export const Address = (props: any) => (
  <>{compact([props.row.original.addressLine1?.trim(), props.row.original.addressLine2?.trim()]).join(`, `)}</>
)

/** Age cell: years since the contact's birth date. Shared by the contact
 * tables (attribution-contacts and marketing-details). */
export const Age = (props: any) => (
  <>{props.row.original.birthDate && (moment().diff(props.row.original.birthDate, `years`) || `0`)}</>
)

/** Columns shared verbatim by the contact tables (attribution-contacts and
 * marketing-details). Extracted so the identical block lives in one place.
 * These columns close over no table-specific handler, so they can be spread
 * directly into each table's column list. */
export const generalContactColumns: ColumnDef<ContactJson, any>[] = [
  {
    id: `leadOwner`,
    header: `Contact Owner`,
    accessorKey: `leadOwner`,
    meta: {
      group: `General`,
      defaultVisible: false,
      pinned: false,
      sortKey: `leadOwner.name.keyword`,
      tooltipClassName: `overflow-visible`,
      ControlledComponent: (row) => <LeadOwnerAssignCell row={row} />,
    },
  },
  {
    id: `Converted By`,
    accessorKey: `convertedBy`,
    header: `Converted By`,
    cell: (props) => <span>{props.row.original.convertedBy?.name}</span>,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `convertedBy.name.keyword`,
    },
  },
  {
    id: `status`,
    accessorFn: (props) => props.patientStatus,
    header: `Patient Status`,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `patientStatus.keyword`,
    },
  },
  {
    id: `Is Converted`,
    accessorKey: `isConverted`,
    header: `Is Converted`,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `isConverted`,
    },
  },
  {
    id: `Is Qualified`,
    accessorKey: `isQualified`,
    header: `Qualification Status`,
    cell: (props) => (props.getValue() === true ? `Qualified` : props.getValue() === false ? `Not Qualified` : ``),
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `isQualified`,
    },
  },
]

/** The Referral URL / First Contacted Reason / Contact ID columns, shared
 * verbatim by attribution-contacts and marketing-details. They appear at
 * different positions in each table, so this is a separate spreadable block. */
export const marketingReferralColumns: ColumnDef<ContactJson, any>[] = [
  {
    id: `referralURL`,
    accessorKey: `referralURL`,
    header: `Referral URL`,
    meta: {
      group: `Marketing`,
      defaultVisible: false,
      sortKey: `referralURL.keyword`,
      tooltipClassName: `lowercase`,
    },
    enableSorting: false,
  },
  {
    id: `firstContactReason`,
    accessorKey: `firstContactReason`,
    header: `First Contacted Reason`,
    meta: {
      group: `Marketing`,
      defaultVisible: false,
      sortKey: `firstContactReason.keyword`,
    },
  },
  {
    id: `id`,
    accessorKey: `id`,
    header: `Contact ID`,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `id.keyword`,
    },
  },
]

/** Contact-detail columns (phones, email, address, demographics, PMS link)
 * shared verbatim by the contact tables (attribution-contacts and
 * marketing-details). These columns close over no table-specific handler, so
 * they can be spread directly into each table's column list. */
export const contactDetailColumns: ColumnDef<ContactJson, any>[] = [
  {
    id: `homePhone`,
    accessorKey: `homePhone`,
    header: `Home Phone`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      pinned: false,
      sortKey: `homePhone.keyword`,
    },
    cell: (props) => <PhoneNumber number={props.row.original.homePhone} />,
  },
  {
    id: `cellPhone`,
    accessorKey: `cellPhone`,
    header: `Cell Phone`,
    cell: (props) => <PhoneNumber number={props.row.original.cellPhone} />,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      pinned: false,
      sortKey: `cellPhone.keyword`,
    },
  },
  {
    id: `workPhone`,
    accessorKey: `workPhone`,
    header: `Work Phone`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      pinned: false,
      sortKey: `workPhone.keyword`,
    },
    cell: (props) => <PhoneNumber number={props.row.original.workPhone} />,
  },
  {
    id: `Other Phone`,
    accessorKey: `otherPhone`,
    header: `Other Phone`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      sortKey: `otherPhone.keyword`,
    },
  },
  {
    id: `email`,
    accessorKey: `email`,
    header: `Email`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      sortKey: `email.keyword`,
    },
  },
  {
    id: `address`,
    accessorKey: `addressLine1`,
    header: `Address`,
    cell: Address,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      sortKey: `addressLine1.keyword`,
    },
  },
  {
    id: `city`,
    accessorKey: `city`,
    header: `City`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      sortKey: `city.keyword`,
    },
  },
  {
    id: `State`,
    accessorKey: `state`,
    header: `State`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      sortKey: `state.keyword`,
    },
  },
  {
    id: `zip`,
    accessorKey: `zip`,
    header: `Zipcode`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      sortKey: `zip.keyword`,
    },
  },
  {
    id: `birthDate`,
    accessorKey: `birthDate`,
    header: `Age`,
    cell: Age,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `birthDate`,
    },
  },
  {
    id: `Gender`,
    accessorKey: `gender`,
    header: `Gender`,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `gender.keyword`,
    },
  },
  {
    id: `Patient Linked`,
    accessorKey: `patientIds`,
    header: `Patient Linked`,
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `patientIds.keyword`,
    },
    cell: (props) => (
      <span>
        {props.getValue() ? (
          <CheckCircleIcon className="h-5 w-5 fill-current text-blue-400" />
        ) : (
          <XmarkCircle className="h-5 w-5 text-red-400" />
        )}
      </span>
    ),
  },
]

/** Identity columns (patient/preferred name, name source, workspace) shared
 * verbatim by the contact tables (attribution-contacts and marketing-details).
 * A standalone array so each table spreads it at its own position. */
export const contactIdentityColumns: ColumnDef<ContactJson, any>[] = [
  {
    id: `Patient Name`,
    accessorKey: `patientName`,
    header: `Patient Name`,
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `patientName.keyword`,
    },
  },
  {
    id: `Preferred Name`,
    accessorKey: `preferredName`,
    header: `Preferred Name`,
    meta: {
      group: `Communication`,
      defaultVisible: false,
      sortKey: `preferredName.keyword`,
    },
  },
  {
    id: `Name Source`,
    accessorKey: `nameSource`,
    header: `Name Source`,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `nameSource.keyword`,
    },
  },
  {
    id: `workspaceId`,
    accessorKey: `workspaceId`,
    header: `Workspace`,
    cell: (props) => <LazyWorkspaceCell workspaceId={props.row.original.workspaceId} />,
    meta: {
      group: `General`,
      defaultVisible: false,
    },
    enableSorting: false,
  },
]

/** The "Name" column shared by the contact tables (attribution-contacts and
 * marketing-details). It closes over the table's contactHandler, so it is a
 * factory rather than a static column. */
export const nameColumn = (
  contactHandler: (contactId: string, patientIds?: string[]) => void,
): ColumnDef<ContactJson, any> => ({
  id: `name`,
  accessorKey: `name`,
  header: `Name`,
  enableResizing: true,
  meta: {
    group: `General`,
    defaultVisible: true,
    pinned: true,
    sortKey: `name.plain`,
    ControlledComponent: (row) => (
      <AnchorCell
        href={currentUrlWithParam(`drawer`, row?.id!)}
        tooltipMessage={row?.name ?? ``}
        clickHandler={() => contactHandler(row?.id!, row?.patientIds)}
      >
        <ContactCell contact={row as ContactJson} />
      </AnchorCell>
    ),
  },
  enableHiding: false,
  minSize: 100,
})

/** The "Location" column shared by the contact tables. Static (no closure). */
/** The "Location" column shared by the contact tables and any table whose row
 * has a `locationId`. A generic factory so it works for both the contact row
 * type and the revenue transaction row type. */
export const locationColumn = <T extends { locationId?: string | null }>(): ColumnDef<T, any> => ({
  id: `locationId`,
  accessorKey: `locationId`,
  header: `Location`,
  cell: (props) => <LazyLocationCell locationId={props.row.original.locationId!} />,
  meta: {
    group: `General`,
    defaultVisible: true,
  },
  enableSorting: false,
})

/** The "Tags" column shared by the contact tables (attribution-contacts,
 * marketing-details) and the activity tables (CallsModal). A generic factory
 * because the row type (contact vs activity) and the TagsCell `model`
 * (`contacts` vs `activities`) differ per table. */
export const tagsColumn = <T extends { id?: string | null; tags?: unknown; workspaceId?: string | null }>(
  model: `contacts` | `activities` = `contacts`,
): ColumnDef<T, any> => ({
  id: `tags`,
  accessorFn: (row) => row.tags,
  header: `Tags`,
  meta: {
    group: `General`,
    defaultVisible: true,
    ControlledComponent: (row) => (
      <TagsCell tags={row?.tags as any} workspaceId={row?.workspaceId ?? undefined} model={model} modelId={row?.id!} />
    ),
  },
  enableSorting: false,
})

/** The "Channel" column shared by the contact tables. */
export const channelColumn: ColumnDef<ContactJson, any> = {
  id: `channel`,
  accessorKey: `channel`,
  header: `Channel`,
  meta: {
    group: `Marketing`,
    defaultVisible: true,
    sortKey: `channel.keyword`,
    ControlledComponent: (row) => <ChannelMappingCell contact={row} />,
  },
}

/** The UTM marketing columns (Source, Medium, Campaign, Content) shared by the
 * contact tables (attribution-contacts, marketing-details) and the main
 * contacts table. Source/Medium/Campaign are inline-editable (unless isSMC) and
 * share one `defaultVisible` flag; Content is always hidden by default. */
export const utmEditableColumns = (args: {
  isSMC: boolean
  updateContact: (contact: UpdateContactInput & { id?: string }) => void
  closeDrawerHandler: () => void
  defaultVisible: boolean
}): ColumnDef<ContactJson, any>[] => {
  const { isSMC, updateContact, closeDrawerHandler, defaultVisible } = args
  const editable = (
    id: string,
    header: string,
    key: `utmSource` | `utmMedium` | `utmCampaign`,
    defaultEmptyValue: string,
  ): ColumnDef<ContactJson, any> => ({
    id,
    header,
    accessorKey: key,
    meta: {
      group: `Marketing`,
      defaultVisible,
      sortKey: `${key}.keyword`,
      tooltipClassName: `lowercase`,
      ControlledComponent: (row) =>
        isSMC ? (
          <TooltipWrapper value={row?.[key]} />
        ) : (
          <InlineEditCell
            value={row?.[key]}
            defaultEmptyValue={defaultEmptyValue}
            className="text-body-xs text-default"
            handleSubmit={(value) => {
              if (row?.[key] === value) return
              updateContact({ id: row?.id, [key]: value })
            }}
            triggerEditSideEffect={closeDrawerHandler}
          />
        ),
    },
  })
  return [
    editable(`utmSource`, `Source`, `utmSource`, `direct`),
    editable(`utmMedium`, `Medium`, `utmMedium`, `(not set)`),
    editable(`utmCampaign`, `Campaign`, `utmCampaign`, `(not set)`),
    {
      id: `content`,
      accessorKey: `utmContent`,
      header: `Content`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `utmContent.keyword`,
        tooltipClassName: `lowercase`,
      },
    },
  ]
}

/** The "Ad Platform" column shared by the contact tables (contacts,
 * marketing-details) and attribution. */
export const adPlatformColumn: ColumnDef<ContactJson, any> = {
  id: `platform`,
  header: `Ad Platform`,
  accessorKey: `platform`,
  meta: {
    group: `Marketing`,
    defaultVisible: false,
    sortKey: `platform.keyword`,
    ControlledComponent: (row) => <AdPlatformMappingCell contact={row} />,
  },
}

/** The "Reported Attribution" column shared by the contact tables (contacts,
 * marketing-details). */
export const reportedAttributionColumn: ColumnDef<ContactJson, any> = {
  id: `reportedAttribution`,
  header: `Reported Attribution`,
  accessorKey: `reportedAttribution`,
  meta: {
    getCellStyle: () => `lowercase`,
    group: `Marketing`,
    defaultVisible: false,
    sortKey: `reportedAttribution.keyword`,
  },
}

/** Applies the marketing/UTM column permission gating shared by the contact
 * tables (contacts, marketing-details): the Marketing columns need the advanced
 * marketing permission (or super), and the UTM columns additionally require the
 * see-utm permission and a non-SMC account. */
export const marketingUtmColumnsWithPermissions = (args: {
  isSuper: boolean
  isSMC: boolean
  checkPermission: (permission: Permission | Permission[]) => boolean
  marketingColumns: ColumnDef<ContactJson, any>[]
  utmColumns: ColumnDef<ContactJson, any>[]
}): ColumnDef<ContactJson, any>[] => {
  const { isSuper, isSMC, checkPermission, marketingColumns, utmColumns } = args
  const addMarketing = isSuper || checkPermission(`org:marketing:advanced-filters`)
  const addUtm = !isSMC && (isSuper || checkPermission(`org:contacts:see-utm`))
  return compact([...(addMarketing ? marketingColumns : []), ...(addUtm ? utmColumns : [])])
}

/** The "Scheduled Production" dollar column shared by the contact tables
 * (attribution-contacts, marketing-details). */
export const scheduledProductionColumn: ColumnDef<ContactJson, any> = {
  id: `Scheduled Production`,
  accessorKey: `scheduledProduction`,
  cell: (info) => `$${info.getValue()?.toLocaleString(`en-US`) ?? 0}`,
  header: `Scheduled Production`,
  meta: {
    group: `General`,
    defaultVisible: true,
    sortKey: `scheduledProduction`,
  },
}

/** The "Net Production" dollar column shared by the attribution and
 * marketing-details contact tables. */
export const productionLtvColumn: ColumnDef<ContactJson, any> = {
  id: `productionLtv`,
  accessorKey: `productionLtv`,
  accessorFn: (row) => `$${row.productionLtv?.toLocaleString(`en-US`) ?? 0}`,
  cell: (info) => info.getValue(),
  header: `Net Production`,
  meta: {
    group: `General`,
    defaultVisible: true,
    sortKey: `productionLtv`,
  },
}

/** The "Collected" dollar column shared by the attribution and
 * marketing-details contact tables. */
export const collectedLtvColumn: ColumnDef<ContactJson, any> = {
  id: `collectedLtv`,
  accessorKey: `collectedLtv`,
  accessorFn: (row) => `$${row.collectedLtv?.toLocaleString(`en-US`) ?? 0}`,
  cell: (info) => info.getValue(),
  header: `Collected`,
  meta: {
    group: `General`,
    defaultVisible: false,
    sortKey: `collectedLtv`,
  },
}

/** The "Appointment Status" and "Patient Referral" columns shared by the
 * attribution and marketing-details contact tables. */
export const appointmentReferralColumns: ColumnDef<ContactJson, any>[] = [
  {
    id: `appointmentStatus`,
    accessorKey: `appointmentStatus`,
    header: `Appointment Status`,
    meta: {
      group: `General`,
      defaultVisible: false,
      sortKey: `appointmentStatus.keyword`,
    },
  },
  {
    id: `patientReferral`,
    accessorKey: `patientReferral`,
    header: `Patient Referral`,
    meta: {
      group: `PMS`,
      defaultVisible: false,
      sortKey: `patientReferral.keyword`,
    },
  },
]

/** The "First Visit Value" dollar column shared by the attribution and
 * marketing-details contact tables. */
export const firstVisitValueColumn: ColumnDef<ContactJson, any> = {
  id: `First Visit Value`,
  accessorKey: `fvv`,
  accessorFn: (row) => `$${row.fvv?.toLocaleString(`en-US`) ?? 0}`,
  cell: (info) => info.getValue(),
  header: `First Visit Value`,
  meta: {
    group: `General`,
    defaultVisible: false,
    sortKey: `fvv`,
  },
}

/** Signature of the marketing-details column factory: like ContactColumnsFactory
 * but with a checkPermission argument (used to gate marketing/UTM columns). */
export type MarketingColumnsFactory = (
  contactHandler: (contactId: string, patientIds?: string[]) => void,
  updateContact: (contact: UpdateContactInput & { id?: string }) => void,
  closeDrawerHandler: () => void,
  checkPermission: (permission: Permission | Permission[]) => boolean,
  isSMC: boolean,
  isSuper: boolean,
) => ColumnDef<ContactJson, any>[]
